import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import { AverageLastScoreLocation } from '@sorare/core/src/__generated__/globalTypes';
import { LabelM } from '@sorare/core/src/atoms/typography';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { inventoryMessages } from '@sorare/core/src/lib/glossary';

export enum FilterKey {
  COMPETITION = 'competition',
  TEAM = 'team',
  HOME_AWAY = 'homeAway',
  MODE = 'mode',
  POSITION = 'position',
}

export const ALL_VALUE = 'ALL' as const;
export const ALL_TEAMS_ITEM: {
  value: string;
  label: ReactNode;
} = {
  value: ALL_VALUE,
  label: (
    <LabelM bold>
      <FormattedMessage {...inventoryMessages.allTeams} />
    </LabelM>
  ),
};

export const ALL_COMPETITIONS_ITEM: {
  value: string;
  label: ReactNode;
} = {
  value: ALL_VALUE,
  label: (
    <LabelM bold>
      <FormattedMessage
        id="fixtureChart.competitionFilter.all"
        defaultMessage="Competitions"
      />
    </LabelM>
  ),
};

const homeAwayValues = [
  ALL_VALUE,
  AverageLastScoreLocation.HOME,
  AverageLastScoreLocation.AWAY,
] as const;

const homeAwayLabels = defineMessages({
  [AverageLastScoreLocation.HOME]: {
    id: 'Score.home',
    defaultMessage: 'Home',
  },
  [AverageLastScoreLocation.AWAY]: {
    id: 'Score.away',
    defaultMessage: 'Away',
  },
  [ALL_VALUE]: {
    id: 'Score.all',
    defaultMessage: 'All',
  },
});

export const homeAwayOptions = homeAwayValues.map(val => ({
  value: val,
  label: (
    <LabelM bold>
      <FormattedMessage {...homeAwayLabels[val]} />
    </LabelM>
  ),
}));

type WithAllValue<T> = T | typeof ALL_VALUE;

export type Filters = {
  [FilterKey.COMPETITION]: WithAllValue<string>;
  [FilterKey.TEAM]: WithAllValue<string>;
  [FilterKey.HOME_AWAY]: WithAllValue<AverageLastScoreLocation>;
  [FilterKey.MODE]: TotalAveragePlayerScore;
  [FilterKey.POSITION]: Position;
};

export type Options = {
  [FilterKey.COMPETITION]: {
    value: string;
    label: ReactNode;
  }[];
  [FilterKey.TEAM]: {
    value: string;
    label: ReactNode;
  }[];
  [FilterKey.HOME_AWAY]: {
    value: WithAllValue<AverageLastScoreLocation>;
    label: ReactNode;
  }[];
  [FilterKey.POSITION]: {
    value: Position;
    label: ReactNode;
  }[];
  [FilterKey.MODE]: {
    value: TotalAveragePlayerScore;
    label: ReactNode;
  }[];
};
